import { Button, Result } from '@surecompanies/core_components'
import React from 'react'
import { classifyError, type ApiErrorKind } from '../utils/api-errors'

interface QueryErrorStateProps {
  error: unknown
  onRetry?: () => void
  /** Shown for a 404, e.g. "Run not found". */
  notFoundTitle?: string
  notFoundSubTitle?: string
}

interface ErrorCopy {
  status: 'warning' | '403' | '404' | '500'
  title: string
  subTitle: string
}

function getCopy(
  kind: ApiErrorKind,
  notFoundTitle: string,
  notFoundSubTitle: string
): ErrorCopy {
  switch (kind) {
    case 'forbidden':
      return {
        status: '403',
        title: 'No access',
        subTitle: 'You do not have access to the enrollment overlap audit.',
      }
    case 'not-found':
      return {
        status: '404',
        title: notFoundTitle,
        subTitle: notFoundSubTitle,
      }
    case 'bad-request':
      return {
        status: 'warning',
        title: 'Invalid request',
        subTitle:
          'The link contains an invalid page size, page number or identifier.',
      }
    default:
      return {
        status: '500',
        title: 'Something went wrong',
        subTitle: 'The data could not be loaded. Please try again.',
      }
  }
}

/**
 * One place for the error states every screen needs: 400 (bad paging/id),
 * 403 (no access), 404 (unknown run/employee) and a retryable generic error.
 */
export default function QueryErrorState({
  error,
  onRetry,
  notFoundTitle = 'Not found',
  notFoundSubTitle = 'It may have been removed or the link is incorrect.',
}: QueryErrorStateProps) {
  const kind = classifyError(error)
  const copy = getCopy(kind, notFoundTitle, notFoundSubTitle)

  return (
    <Result
      status={copy.status}
      title={copy.title}
      subTitle={copy.subTitle}
      extra={
        kind === 'server' && onRetry ? (
          <Button type="primary" onClick={onRetry}>
            Retry
          </Button>
        ) : null
      }
    />
  )
}
